import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@restore/store';
import { produce } from 'immer';
import { ClinicalTrialDetailType } from '@dynamika-shared/types/clinicalTrials';
import {
  ClinicalTrialQuestionnaire,
  Questionnaire,
} from '@dynamika-shared/types/questionnaire';
import { CohortType } from '@dynamika-shared/types/cohorts';
import { VisitConfig } from '@dynamika-shared/types/visits';

const selectClinicalTrialData = (state: RootState) =>
  state.clinicalTrial.data as ClinicalTrialDetailType;
const selectClinicalTrialQuestionnaires = (state: RootState) =>
  state.questionnaires.clinicalTrialQuestionnaires;

const updateQuestionnaires =
  (questionnaires: Questionnaire[]) => (q: ClinicalTrialQuestionnaire) => {
    const matchingQuestionnaire = questionnaires?.find(
      ({ id }) => id === q.clinicalTrialQuestionnaireId
    );
    q.name = matchingQuestionnaire?.name;
    q.alias = matchingQuestionnaire?.alias;
    return q;
  };

const updateVisits =
  (questionnaires: Questionnaire[]) => (visit: VisitConfig) => {
    visit.questionnaires = visit.questionnaires?.map(
      updateQuestionnaires(questionnaires)
    );
    return visit;
  };

const updateCohorts =
  (questionnaires: Questionnaire[]) => (cohort: CohortType) => {
    cohort.visits = cohort.visits
      ?.map(updateVisits(questionnaires))
      .sort((a, b) => a.daysFromBaseline - b.daysFromBaseline);
    return cohort;
  };

export const selectClinicalTrialWithFullQuestionnaireInfo = createSelector(
  [selectClinicalTrialData, selectClinicalTrialQuestionnaires],
  (data, questionnaires) => {
    return produce(data, draft => {
      draft.questionnaires = draft.questionnaires?.map(q => {
        const matchingQuestionnaire = questionnaires?.find(
          ({ id }) => id === q.clinicalTrialQuestionnaireId
        );
        q.name = matchingQuestionnaire?.name;
        q.alias = matchingQuestionnaire?.alias;
        return q;
      });

      draft.cohorts = draft.cohorts
        ?.map(updateCohorts(questionnaires))
        .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    });
  }
);
